import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Translate as TranslateIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useProjects,
  useUpdateProject,
  useDeleteProject,
} from "../../hooks/useProjects";
import { useTechnologies } from "../../hooks/useTechnologies";
import { supabase } from "../../lib/supabase";
import {
  adminColors,
  textFieldStyles,
  selectMenuProps,
  dialogStyles,
  tableStyles,
  buttonStyles,
  switchStyles,
  chipStyles,
} from "./adminStyles";
import { useTranslation } from "react-i18next";

const AdminProjects = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    data: projects,
    isLoading,
    error,
    refetch,
  } = useProjects({ isPublished: null });
  const { data: technologies } = useTechnologies({ language: i18n.language });
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    slug: "",
    github_url: "",
    live_url: "",
    cover_image_url: "",
    is_published: false,
    type: "project",
    selectedTechs: [],
    translations: {
      fr: { title: "", description: "" },
      en: { title: "", description: "" },
    },
  });

  const handleOpenCreate = () => {
    setSelectedProject(null);
    setFormData({
      slug: "",
      github_url: "",
      live_url: "",
      cover_image_url: "",
      is_published: false,
      type: "project",
      selectedTechs: [],
      translations: {
        fr: { title: "", description: "" },
        en: { title: "", description: "" },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (project) => {
    setSelectedProject(project);
    const frTrans =
      project.translations?.find((t) => t.language_code === "fr") || {};
    const enTrans =
      project.translations?.find((t) => t.language_code === "en") || {};

    // Fetch related techs
    const { data: relTechs } = await supabase
      .from("project_technologies")
      .select("technology_id")
      .eq("project_id", project.id);

    const initialTechs = relTechs
      ? technologies?.filter((t) =>
          relTechs.some((rt) => rt.technology_id === t.id)
        ) || []
      : [];

    setFormData({
      slug: project.slug || "",
      github_url: project.github_url || "",
      live_url: project.live_url || "",
      cover_image_url: project.cover_image_url || "",
      is_published: project.is_published || false,
      type: project.type || "project",
      selectedTechs: initialTechs,
      translations: {
        fr: {
          title: frTrans.title || "",
          description: frTrans.description || "",
        },
        en: {
          title: enTrans.title || "",
          description: enTrans.description || "",
        },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenDelete = (project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleMove = async (project, direction) => {
    const currentIndex = projects.findIndex((p) => p.id === project.id);
    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= projects.length) return;

    const targetProject = projects[targetIndex];

    try {
      // Swap display_order
      const tempOrder = project.display_order || 0;
      await supabase
        .from("projects")
        .update({ display_order: targetProject.display_order || 0 })
        .eq("id", project.id);
      await supabase
        .from("projects")
        .update({ display_order: tempOrder })
        .eq("id", targetProject.id);
      await refetch();
    } catch (err) {
      console.error("Error reordering projects:", err);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const projectData = {
        slug: formData.slug,
        github_url: formData.github_url || null,
        live_url: formData.live_url || null,
        cover_image_url: formData.cover_image_url || null,
        is_published: formData.is_published,
        type: formData.type,
      };

      let projectId;

      if (selectedProject) {
        await updateProject.mutateAsync({
          id: selectedProject.id,
          ...projectData,
        });
        projectId = selectedProject.id;

        // Update translations
        for (const lang of ["fr", "en"]) {
          const transData = {
            project_id: projectId,
            language_code: lang,
            title: formData.translations[lang].title,
            description: formData.translations[lang].description,
          };

          const { data: existing } = await supabase
            .from("projects_translations")
            .select("id")
            .eq("project_id", projectId)
            .eq("language_code", lang)
            .maybeSingle();

          if (existing) {
            const { error: transError } = await supabase
              .from("projects_translations")
              .update(transData)
              .eq("id", existing.id);
            if (transError) throw transError;
          } else {
            const { error: transError } = await supabase
              .from("projects_translations")
              .insert({ id: crypto.randomUUID(), ...transData });
            if (transError) throw transError;
          }
        }

        // Update technologies
        const { error: techDelError } = await supabase
          .from("project_technologies")
          .delete()
          .eq("project_id", projectId);
        if (techDelError) throw techDelError;
      } else {
        // For creation, we might not get the returned row if RLS is tight,
        // but we need the ID. Let's generate it ourselves to be safe.
        const newId = crypto.randomUUID();
        const { error: createError } = await supabase
          .from("projects")
          .insert({ id: newId, ...projectData });

        if (createError) throw createError;
        projectId = newId;

        for (const lang of ["fr", "en"]) {
          const { error: transError } = await supabase
            .from("projects_translations")
            .insert({
              id: crypto.randomUUID(),
              project_id: projectId,
              language_code: lang,
              title: formData.translations[lang].title,
              description: formData.translations[lang].description,
            });
          if (transError) throw transError;
        }
      }

      // Batch insert technologies
      if (formData.selectedTechs.length > 0) {
        const techInserts = formData.selectedTechs.map((tech) => ({
          project_id: projectId,
          technology_id: tech.id,
        }));
        const { error: techInsError } = await supabase
          .from("project_technologies")
          .insert(techInserts);
        if (techInsError) throw techInsError;
      }

      await refetch();
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving project:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync(selectedProject.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting project:", err);
    }
  };

  const updateTranslation = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], [field]: value },
      },
    }));
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: adminColors.bg.page,
        }}
      >
        <CircularProgress sx={{ color: adminColors.accent.teal }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: "100vh", bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/admin")}
              sx={{ color: adminColors.text.primary }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{ color: adminColors.text.primary, fontWeight: 700 }}
            >
              {t("admin.sections.projects")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={buttonStyles.primary}
          >
            {t("admin.actions.create")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message}
          </Alert>
        )}

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.title")} (FR)
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.slug")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.order")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.category")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.status")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">
                  {t("admin.labels.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      ...tableStyles.bodyCell,
                      textAlign: "center",
                      py: 4,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {t("admin.messages.noData")}
                  </TableCell>
                </TableRow>
              )}
              {projects?.map((project) => (
                <TableRow key={project.id} sx={tableStyles.row}>
                  <TableCell sx={tableStyles.bodyCell}>
                    {project.title || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.bodyCell,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {project.slug}
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Stack direction="row">
                      <IconButton
                        size="small"
                        onClick={() => handleMove(project, "up")}
                        disabled={projects.indexOf(project) === 0}
                        sx={{ color: adminColors.accent.teal }}
                      >
                        <UpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMove(project, "down")}
                        disabled={
                          projects.indexOf(project) === projects.length - 1
                        }
                        sx={{ color: adminColors.accent.teal }}
                      >
                        <DownIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Chip
                      label={project.type}
                      size="small"
                      sx={chipStyles.type(adminColors.accent.teal)}
                    />
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Chip
                      label={
                        project.is_published
                          ? t("admin.labels.published")
                          : t("admin.labels.draft")
                      }
                      size="small"
                      sx={
                        project.is_published
                          ? chipStyles.published
                          : chipStyles.draft
                      }
                    />
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell} align="right">
                    <IconButton
                      onClick={() => handleOpenEdit(project)}
                      sx={buttonStyles.iconEdit}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDelete(project)}
                      sx={buttonStyles.iconDelete}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: adminColors.text.primary,
              borderBottom: `1px solid ${adminColors.border.default}`,
              pb: 2,
            }}
          >
            <TranslateIcon sx={{ color: adminColors.accent.teal }} />
            {selectedProject
              ? `${t("admin.actions.edit")} ${t("admin.sections.projects")}`
              : `${t("admin.actions.create")} ${t("admin.sections.projects")}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t("admin.labels.category")}
                  select
                  fullWidth
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  sx={textFieldStyles}
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: selectMenuProps } },
                  }}
                >
                  <MenuItem value="project">
                    {t("admin.sections.projects")}
                  </MenuItem>
                  <MenuItem value="lab">{t("admin.sections.labs")}</MenuItem>
                </TextField>

                <TextField
                  label={`${t("admin.labels.slug")} (URL)`}
                  fullWidth
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                  helperText={t("admin.messages.slugHelper")}
                  sx={textFieldStyles}
                />
              </Stack>

              <Autocomplete
                multiple
                options={technologies || []}
                getOptionLabel={(option) => option.name}
                value={formData.selectedTechs}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, selectedTechs: newValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.sections.technologies")}
                    placeholder={t("messages.noResults")}
                    sx={textFieldStyles}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.name}
                        {...tagProps}
                        sx={{
                          bgcolor: adminColors.accent.teal,
                          color: "white",
                        }}
                      />
                    );
                  })
                }
                PaperProps={{
                  sx: { bgcolor: adminColors.bg.card, color: "white" },
                }}
              />

              <Divider sx={{ borderColor: adminColors.border.default }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: adminColors.accent.gold,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: 600,
                  }}
                >
                  <TranslateIcon fontSize="small" />{" "}
                  {t("admin.translations.title")}
                </Typography>
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => setTabValue(v)}
                  sx={{
                    mb: 2,
                    "& .MuiTabs-indicator": {
                      bgcolor: adminColors.accent.teal,
                    },
                  }}
                >
                  <Tab
                    label={t("admin.translations.tabs.fr")}
                    sx={{
                      color:
                        tabValue === 0
                          ? adminColors.accent.teal
                          : adminColors.text.secondary,
                      fontWeight: tabValue === 0 ? 600 : 400,
                    }}
                  />
                  <Tab
                    label={t("admin.translations.tabs.en")}
                    sx={{
                      color:
                        tabValue === 1
                          ? adminColors.accent.teal
                          : adminColors.text.secondary,
                      fontWeight: tabValue === 1 ? 600 : 400,
                    }}
                  />
                </Tabs>

                {tabValue === 0 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t("admin.labels.title")} (FR)`}
                      fullWidth
                      value={formData.translations.fr.title}
                      onChange={(e) =>
                        updateTranslation("fr", "title", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.description")} (FR)`}
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.translations.fr.description}
                      onChange={(e) =>
                        updateTranslation("fr", "description", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}

                {tabValue === 1 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t("admin.labels.title")} (EN)`}
                      fullWidth
                      value={formData.translations.en.title}
                      onChange={(e) =>
                        updateTranslation("en", "title", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.description")} (EN)`}
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.translations.en.description}
                      onChange={(e) =>
                        updateTranslation("en", "description", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}
              </Box>

              <Divider sx={{ borderColor: adminColors.border.default }} />

              <TextField
                label="GitHub URL"
                fullWidth
                value={formData.github_url}
                onChange={(e) =>
                  setFormData({ ...formData, github_url: e.target.value })
                }
                sx={textFieldStyles}
              />
              <TextField
                label="Live URL"
                fullWidth
                value={formData.live_url}
                onChange={(e) =>
                  setFormData({ ...formData, live_url: e.target.value })
                }
                sx={textFieldStyles}
              />
              <TextField
                label={t("admin.labels.coverImageUrl")}
                fullWidth
                value={formData.cover_image_url}
                onChange={(e) =>
                  setFormData({ ...formData, cover_image_url: e.target.value })
                }
                sx={textFieldStyles}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_published}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_published: e.target.checked,
                      })
                    }
                    sx={switchStyles}
                  />
                }
                label={t("admin.labels.published")}
                sx={{ color: adminColors.text.primary }}
              />
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{ p: 3, borderTop: `1px solid ${adminColors.border.default}` }}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saving}
              sx={buttonStyles.primary}
            >
              {saving ? (
                <CircularProgress
                  size={24}
                  sx={{ color: adminColors.bg.page }}
                />
              ) : (
                t("admin.actions.save")
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle sx={{ color: adminColors.text.primary }}>
            {t("admin.messages.confirmDeleteTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: adminColors.text.secondary }}>
              {t("admin.messages.confirmDelete")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              disabled={deleteProject.isPending}
              sx={{
                bgcolor: adminColors.status.error,
                "&:hover": { bgcolor: "#DC2626" },
              }}
            >
              {deleteProject.isPending ? (
                <CircularProgress size={24} />
              ) : (
                t("admin.actions.delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminProjects;
